"use client";

import { useState } from "react";
import { TextField, Box, Alert } from "@mui/material";

export default function LeadForm() {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    phone: "",
    zip: "",
    comments: "",
    product: "Insulation",
    leadType: "",
  });

  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");

  // auto-format phone
  const handlePhoneChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const rawValue = e.target.value.replace(/\D/g, "");
    let formatted = rawValue;

    if (rawValue.length > 3 && rawValue.length <= 6) {
      formatted = `(${rawValue.slice(0, 3)}) ${rawValue.slice(3)}`;
    } else if (rawValue.length > 6) {
      formatted = `(${rawValue.slice(0, 3)}) ${rawValue.slice(3, 6)}-${rawValue.slice(6, 10)}`;
    }

    setFormState({ ...formState, phone: formatted });
  };
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.target.name === "phone") return handlePhoneChange(e);
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    // Zip must be 5 digits
    const zipRegex = /^\d{5}$/;
    if (!zipRegex.test(formState.zip)) {
      setErrorMessage("Zip code must be 5 digits");
      return false;
    }

    // Email must be valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formState.email)) {
      setErrorMessage("Enter a valid email address");
      return false;
    }

    // Phone must be 10 digits (remove formatting)
    const phoneDigits = formState.phone.replace(/\D/g, "");
    if (phoneDigits.length !== 10) {
      setErrorMessage("Phone number must be 10 digits");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    if (!validateForm()) {
      setStatus("error");
      return;
    }
    const basePayload = {
      ...formState,
      leadType: "Affordable",
    };
    let payload = {
      ...basePayload,
      phone: basePayload.phone.replace(/\D/g, ""),
    };

    payload = {
      ...payload,
      product: "Insulation",
    };

    const formJson = JSON.stringify(payload);
    try {
      const res = await fetch("/api/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: formJson,
      });

      const data = await res.json();

      if (!res.ok) {
        setStatus("error");
        setErrorMessage(data.error || "Submission failed");
        return;
      }

      setStatus("success");
      setFormState({
        name: "",
        email: "",
        phone: "",
        zip: "",
        product: "Insulation",
        leadType: "",
        comments: "",
      });
    } catch (err) {
      setStatus("error");
      setErrorMessage("Server error");
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <p className="form-title">Request an Inspection</p>
      {status !== "success" && (
        <>
          <TextField
            label="Name"
            name="name"
            value={formState.name}
            onChange={handleChange}
            required
          />
          <TextField
            label="Email"
            type="email"
            name="email"
            value={formState.email}
            onChange={handleChange}
            required
          />
          <TextField
            label="Phone"
            type="tel"
            name="phone"
            value={formState.phone}
            onChange={handleChange}
            required
            helperText="Enter 10-digit phone number"
          />
          <TextField
            label="Zip"
            name="zip"
            value={formState.zip}
            onChange={handleChange}
            required
            helperText="Enter 5-digit zip code"
          />
          <TextField
            label="Comments"
            name="comments"
            multiline
            value={formState.comments}
            onChange={handleChange}
            required
            helperText="Comments or questions?"
          />
          <button
            type="submit"
            className="affordable-submit"
            color="primary"
            disabled={status === "submitting"}
          >
            {status === "submitting" ? "Submitting..." : "Get a Quote"}
          </button>
        </>
      )}
      {status === "success" && (
        <Alert severity="success">
          <a href={process.env.NEXT_PUBLIC_AFFORDABLE_CALENDLY_LINK || ""}>
            {" "}
            Thanks! A team member will reach out to you shortly. To self
            schedule, click here
          </a>
        </Alert>
      )}
      {status === "error" && <Alert severity="error">{errorMessage}</Alert>}
    </Box>
  );
}
